import React from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useSample } from "../utils/useSample";
import { Box } from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import dayjs from "dayjs";
import useFilteredSamplingPoints from "../../../hooks/useFilteredSamplingPoints";
import { IFormSamplesInputs } from "@app/types/samples.types";
import {
  useAnalysisTypes,
  useInstallation,
  useSamplingReason,
  useTypeSample,
} from "@services/servicesGenericsTables";
import { useTypesAccumulation } from "@services/servicesGenericsTables/useTypesAccumulation";
import { InstallationTypes } from "@app/types/genericTables.types";
import {
  ContainerSection,
  MAutocomplete,
  MButton,
  MDatePicker,
  MTextField,
  ResponsiveFormContainer,
} from "@app/components";
import { yearsTable } from "@app/utils/tables/years.tables";
import { yesNoTable } from "@app/utils/tables/shared.tables";
import { weeksTable } from "@app/utils/tables/weeks.tables";

export enum TYPES_RECEPTION {
  YES = "SI",
  NO = "NO",
}

//const FilterSample = () => {
const FilterSample = ({
  onFilterSubmit,
}: {
  onFilterSubmit: (data: IFormSamplesInputs) => void;
}) => {
  const { clientReferenceList, maslReferenceList, setQuery } = useSample();
  const { installationList } = useInstallation();
  const { analysisTypeList } = useAnalysisTypes();
  const { sampleTypeList } = useTypeSample();
  const { samplingReasonList } = useSamplingReason();
  const { typesAccumulationList } = useTypesAccumulation();

  const form = useForm<IFormSamplesInputs>({
    mode: "all",
  });
  const installation = form.watch("installation");
  const isReception = form.watch("is_reception");

  const filteredSamplingPoints = useFilteredSamplingPoints(
    installation as InstallationTypes,
    installation && "sampling_points"
  );

  const submit = (data: IFormSamplesInputs) => {
    setQuery(data);
    onFilterSubmit(data);
  };

  const clearFilters = () => {
    form.reset();
    setQuery({});
  };

  return (
    <ContainerSection label="Filtros">
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(submit)}>
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <ResponsiveFormContainer columns={7}>
              <MAutocomplete
                key={JSON.stringify(form.watch("installation"))}
                name="installation"
                options={installationList}
                label="Instalación"
                getOptionLabel={(option) =>
                  `${option.codigo} - ${option.descripcion}`
                }
              />
              <MAutocomplete
                key={JSON.stringify(form.watch("year"))}
                name="year"
                options={yearsTable}
                label="Año"
                getOptionLabel={(option) => `${option}`}
              />
              <MAutocomplete
                key={JSON.stringify(form.watch("start_week"))}
                name="start_week"
                options={weeksTable}
                label="Semana inicio recogida"
                getOptionLabel={(option) => `${option}`}
              />
              <MAutocomplete
                key={JSON.stringify(form.watch("end_week"))}
                name="end_week"
                options={weeksTable}
                label="Semana final recogida"
                getOptionLabel={(option) => `${option}`}
              />
              <MAutocomplete
                key={JSON.stringify(form.watch("reason"))}
                name="reason"
                options={samplingReasonList}
                label="Motivo"
                getOptionLabel={(option) =>
                  `${option.codigo} - ${option.descripcion}`
                }
              />
              <MAutocomplete
                key={JSON.stringify(filteredSamplingPoints)}
                name="sampling_points"
                options={filteredSamplingPoints}
                label="Punto de toma de muestra"
                getOptionLabel={(option) =>
                  `${option.codigo} - ${option.descripcion}`
                }
                disabled={!installation}
              />
              <MAutocomplete
                key={JSON.stringify(form.watch("sample_type"))}
                name="sample_type"
                options={sampleTypeList}
                label="Tipo de muestra"
                getOptionLabel={(option) =>
                  `${option.codigo} - ${option.descripcion}`
                }
              />
              <MAutocomplete
                key={JSON.stringify(form.watch("shared"))}
                name="shared"
                options={yesNoTable}
                label="Compartida"
                getOptionLabel={(option) => option.label || ""}
              />
              <MTextField
                name="client_reference"
                label="Referencia cliente"
                placeholder="Referencia cliente"
                fullWidth
              />
              <MTextField
                name="masl_reference"
                label="Referencia MASL"
                placeholder="Referencia MASL"
                fullWidth
              />
              <MAutocomplete
                key={JSON.stringify(form.watch("accumulation"))}
                name="accumulation"
                options={typesAccumulationList}
                label="Acumulación"
                getOptionLabel={(option) => `${option.code} - ${option.name}`}
              />
              <MAutocomplete
                key={JSON.stringify(form.watch("analysis"))}
                name="analysis"
                options={analysisTypeList}
                label="Análisis"
                getOptionLabel={(option) => option.descripcion}
              />
              <MAutocomplete
                key={JSON.stringify(form.watch("is_reception"))}
                name="is_reception"
                label="Recepcionada"
                options={[TYPES_RECEPTION.YES, TYPES_RECEPTION.NO]}
              />
              {isReception === TYPES_RECEPTION.YES && (
                <MDatePicker
                  name="date_from"
                  pickerType="date"
                  label="Fecha recepción desde"
                  maxDate={
                    form.watch("date_from") && form.watch("date_to")
                      ? dayjs(form.watch("date_to"))
                      : undefined
                  }
                />
              )}
              {isReception === TYPES_RECEPTION.YES && (
                <MDatePicker
                  name="date_to"
                  pickerType="date"
                  label="Fecha recepción hasta"
                  minDate={
                    form.watch("date_from") && form.watch("date_to")
                      ? dayjs(form.watch("date_from"))
                      : undefined
                  }
                />
              )}
              <Box
                sx={{ display: "flex", alignItems: "center", marginLeft: 2 }}
              >
                <MButton
                  variant="outlined"
                  color="primary"
                  sx={{ textTransform: "uppercase" }}
                  text="Filtrar"
                  type="submit"
                  startIcon={<FilterAltIcon />}
                />
                <MButton
                  variant="outlined"
                  color="secondary"
                  sx={{ textTransform: "uppercase", ml: 2 }}
                  text="Limpiar"
                  onClick={clearFilters}
                />
              </Box>
            </ResponsiveFormContainer>
          </Box>
        </form>
      </FormProvider>
    </ContainerSection>
  );
};

export default FilterSample;
